import { useMemo, useState } from "react";

import {
  useAdminUsers,
  useConsoleAccessLog,
  useCreateAdminUser,
  useLookups,
  useSetAdminUserRoles,
  useSetAdminUserStatus
} from "src/api/hooks";
import { AuditEventList } from "src/components/audit-rows";
import { ReadFailure } from "src/components/read-failure";
import {
  Badge,
  Button,
  Card,
  Empty,
  Field,
  Input,
  RadioGroup,
  type RadioOption,
  Table
} from "src/components/ui";
import type { Role } from "src/graphql/generated";
import { buildAuditNames, groupAuditRows } from "src/lib/audit";
import type { Viewer } from "src/screens/types";

/**
 * What someone can do in one population, as a person would describe it.
 *
 * The five roles are the storage format, not the vocabulary an operator should have
 * to think in. A writer's grants are a strict superset of the matching reader's
 * (`writerPermissions` in `internal/auth/role.go` is
 * `[PermRead, PermCreate, PermUpdate, PermLifecycle]`), so each population is really
 * one three-valued setting rather than two independent checkboxes — and expressing it
 * that way makes the redundant pair, writer *and* reader, unrepresentable.
 */
type Level = "NONE" | "READER" | "WRITER";

/**
 * The three levels, as radio options.
 *
 * Short labels on purpose. They sit side by side rather than inside a dropdown, so
 * the row reads as a scale at a glance — which is the whole reason this is no longer
 * a Select. "Reader — can view" had to spell the meaning out because a closed
 * dropdown showed one option with no context; three visible options are their own
 * context.
 */
const levelOptions: readonly RadioOption<Level>[] = [
  { value: "NONE", label: "No access" },
  { value: "READER", label: "Read" },
  { value: "WRITER", label: "Write" }
];

type Population = "PARTNER" | "ODC";

const populationRoles: Record<Population, { reader: Role; writer: Role }> = {
  PARTNER: { reader: "PARTNER_READER", writer: "PARTNER_WRITER" },
  ODC: { reader: "ODC_READER", writer: "ODC_WRITER" }
};

const populationLabel: Record<Population, string> = {
  PARTNER: "Partners",
  ODC: "On-demand contractors"
};

/** Access is the whole of what a console user may do, in the screen's vocabulary. */
type Access = { admin: boolean; PARTNER: Level; ODC: Level };

function accessOf(roles: readonly Role[]): Access {
  const levelFor = (population: Population): Level => {
    const { reader, writer } = populationRoles[population];
    // Writer first: a user carrying both — granted before this screen made that
    // unrepresentable — reads as the broader of the two, which is what they have.
    if (roles.includes(writer)) {
      return "WRITER";
    }
    return roles.includes(reader) ? "READER" : "NONE";
  };
  return {
    admin: roles.includes("IAM_ADMIN"),
    PARTNER: levelFor("PARTNER"),
    ODC: levelFor("ODC")
  };
}

/**
 * The roles to store for an access setting.
 *
 * iam-admin holds `writerPermissions` in both populations, so it already contains
 * every other role. Storing anything alongside it would persist a grant that confers
 * nothing, so it is stored alone. Saving also normalises a legacy writer+reader pair
 * down to the writer.
 */
function rolesFor(access: Access): Role[] {
  if (access.admin) {
    return ["IAM_ADMIN"];
  }
  const out: Role[] = [];
  for (const population of ["PARTNER", "ODC"] as Population[]) {
    const level = access[population];
    if (level === "WRITER") {
      out.push(populationRoles[population].writer);
    } else if (level === "READER") {
      out.push(populationRoles[population].reader);
    }
  }
  return out;
}

const sameAccess = (a: Access, b: Access) =>
  a.admin === b.admin && a.PARTNER === b.PARTNER && a.ODC === b.ODC;

/**
 * admin_user management, iam-admin only. Every grant and revocation lands in
 * audit_log, which is the reason grants live here rather than in Auth0.
 */
export function AdminUsers({ viewer }: { viewer: Viewer }) {
  const isAdmin = viewer.roles.includes("IAM_ADMIN");
  const { data, isPending, error } = useAdminUsers(isAdmin);
  const create = useCreateAdminUser();
  const setStatus = useSetAdminUserStatus();

  const [email, setEmail] = useState("");
  const [access, setAccess] = useState<Access>({ admin: false, PARTNER: "NONE", ODC: "NONE" });

  // The screen is iam-admin only, which is also why the grant form is: a
  // non-iam-admin never reaches it. The backend requires PermManageAdminUsers on
  // every one of these operations regardless, and the nav hides the tab, so this is
  // the third of three layers rather than the guard itself.
  if (!isAdmin) {
    return (
      <Card title="Console access">
        <p className="text-sm text-slate-600">Only iam-admins can manage console access.</p>
      </Card>
    );
  }

  const users = data?.adminUsers ?? [];
  const grantedRoles = rolesFor(access);

  return (
    <div className="flex flex-col gap-4">
      <Card
        title="Grant console access"
        actions={
          <Button
            // Emptiness only. The address rule lives in the server, which refuses a
            // bad one with a reason that surfaces as a toast; a second copy here
            // would be a second rule to keep in step, and the one it replaced was
            // the very check INFRA-690 was filed about.
            disabled={!email.trim() || grantedRoles.length === 0 || create.isPending}
            title={grantedRoles.length === 0 ? "Give them access to something first" : undefined}
            onClick={() =>
              create.mutate(
                { email: email.trim(), roles: grantedRoles },
                {
                  onSuccess: () => {
                    setEmail("");
                    setAccess({ admin: false, PARTNER: "NONE", ODC: "NONE" });
                  }
                }
              )
            }
          >
            {create.isPending ? "Granting…" : "Grant"}
          </Button>
        }
      >
        <div className="flex flex-col gap-4">
          <Field label="Email" hint="Must be the person's own mailbox, not a group alias" required>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} className="w-72" />
          </Field>
          <AccessControls
            access={access}
            onChange={setAccess}
            disabled={create.isPending}
            idPrefix="grant"
          />
        </div>
      </Card>

      <Card title={`Console users (${users.length})`}>
        {isPending ? <Empty>Loading…</Empty> : null}
        {error ? <ReadFailure error={error} /> : null}
        {users.length > 0 ? (
          <Table head={["Email", "Status", "Access", ""]}>
            {users.map((user) => (
              <UserRow
                key={user.id}
                user={user}
                isSelf={user.id === viewer.adminUserId}
                statusBusy={setStatus.isPending}
                onSetStatus={(status) => setStatus.mutate({ id: user.id, status })}
              />
            ))}
          </Table>
        ) : null}
        <p className="mt-3 text-xs text-slate-500">
          Disabling is the revocation path: the next request from a disabled account is refused, so
          access stops immediately rather than at their next login.
        </p>
      </Card>

      <AccessHistory />
    </div>
  );
}

/**
 * ACCESS_HISTORY_LIMIT is how many changes the panel shows.
 *
 * Named because the panel has to be able to say the number, and because one more than
 * it is what gets requested — see AccessHistory. Well under the server's cap of 500,
 * which leaves the probe row room and keeps this a page rather than a dump.
 */
export const ACCESS_HISTORY_LIMIT = 100;

/**
 * How console access got to be what the list above says it is.
 *
 * The reason this exists: the log has always held every grant, revocation and role
 * change, and no screen showed them, so an access review meant somebody querying
 * audit_log by hand. The list above answers "who has access"; this answers "how did
 * they get it, and who else used to have it".
 *
 * Two things it must never do, both learned the hard way on the organization roster.
 * It must not imply it is showing everything when it is not — so it asks for one more
 * entry than it shows and says which of the two situations it is in, rather than
 * truncating in silence. And it must not report itself truncated when it is in fact
 * complete: a history of exactly ACCESS_HISTORY_LIMIT changes is complete, and the
 * probe row is what tells that apart from the same page with more behind it. Comparing
 * lengths against the cap alone cannot, which is the bug that got filed twice.
 *
 * There is a third way to imply completeness falsely, and it has nothing to do with
 * paging: a claim that was true when the read happened and is not true now. Another
 * administrator granting access in another session appends a row this panel has not
 * seen, and "all 20 changes ever recorded" then describes a database that holds 21.
 * So the claim is made *of the read* — `dataUpdatedAt`, the moment the data arrived —
 * rather than of the present, and useConsoleAccessLog refetches on focus and
 * reconnect so that moment is usually the recent past. Naming the moment is the half
 * that is always true; refreshing is the half that keeps it close to now. The
 * organization roster says when its count was loaded for the same reason.
 *
 * A component of its own rather than a section inline, and that is what hides it from
 * a non-iam-admin: the screen returns before rendering this, so the read is never
 * even mounted. Hiding is convenience and not the control — `unscopedAuditLog`
 * requires PermReadUnscopedAudit of whoever asks, and a refusal renders through
 * ReadFailure, which is reached rather than hypothetical for an admin whose own role
 * is revoked while this page is open.
 */
function AccessHistory() {
  // One more than is shown. Getting it back is proof there are older entries; not
  // getting it is proof there are none, which is the statement an access review needs
  // and the one a bare page of results cannot make.
  const { data, isPending, error, dataUpdatedAt } = useConsoleAccessLog(ACCESS_HISTORY_LIMIT + 1);
  // The shared renderer resolves stored ids to names through these. No admin_user
  // snapshot holds one, so nothing here needs them today — passed because the
  // renderer takes them, and `null` would claim the lookups failed to load rather
  // than that this history has no ids in it.
  const { data: lookups } = useLookups();
  const names = useMemo(() => buildAuditNames(lookups), [lookups]);

  const entries = data?.unscopedAuditLog ?? [];
  const truncated = entries.length > ACCESS_HISTORY_LIMIT;
  const events = groupAuditRows(entries.slice(0, ACCESS_HISTORY_LIMIT));
  // The moment the data arrived, not the moment this renders: a re-render for any
  // other reason must not restate an old read as a fresh one.
  //
  // With the date, not just the clock. Nothing refetches on a timer — `staleTime: 0`
  // does not refetch a mounted query, only focus and reconnect do — so a window that
  // keeps focus overnight would show yesterday's read as "3:42 PM" and read as
  // current, which is the same false freshness a day out instead of an hour. It is
  // also how `occurredAt` is rendered two inches above it in this same card.
  const readAt = new Date(dataUpdatedAt).toLocaleString();

  return (
    <Card title="Access history">
      <AuditEventList
        events={events}
        names={names}
        isPending={isPending}
        error={error}
        // "Changed by" rather than "Who", because on this screen the row names two
        // people: the one who made the change and the one it was made to. The subject
        // is in the What column, so this one has to say which of them it is.
        head={["When", "Changed by", "What"]}
        empty="No console access changes recorded."
      />
      {/* The truncation warning is not conditioned on `error`, and the completeness
          claim is. That asymmetry is the point of it.

          A refetch that fails keeps the data it already had — TanStack sets `error`
          and leaves `data` in place — so an iam-admin whose role is revoked while this
          page is open lands in exactly that state, which the panel calls reachable
          rather than hypothetical. Suppressing both paragraphs there left a hundred
          truncated rows on screen with nothing saying they were truncated: this
          panel's cardinal sin, arrived at through the error path rather than the
          paging one. So a truncated list says so whenever it is on screen.

          The completeness claim is the opposite: it must never be made off a read that
          failed, and it already carries `as of {readAt}` naming the last read that
          did succeed.

          Neither branch tests `isPending`, and one of them used to. The term could not
          fire: `events.length > 0` needs `data`, and a query holding data is not
          pending, so it read as a guard against a state it never saw. It was removed
          rather than kept and explained — a condition that cannot be false is a
          condition a reader has to disprove, and the test that looked like it covered
          this one passed with the term deleted. */}
      {truncated && events.length > 0 ? (
        <p className="mt-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Showing the {ACCESS_HISTORY_LIMIT} most recent changes as of {readAt}. There are older
          ones, which this screen does not show — read audit_log directly for a review that has to
          reach further back than this.
        </p>
      ) : !error && events.length > 0 ? (
        <p className="mt-3 text-xs text-slate-500">
          Complete as of {readAt}: all {events.length} {events.length === 1 ? "change" : "changes"}{" "}
          to console access that had ever been recorded, with nothing older behind them. A change
          made in another session since then is not in this list until it reloads.
        </p>
      ) : null}
      <p className="mt-3 text-xs text-slate-500">
        Every grant, revocation and role change, newest first — which is why access is managed here
        rather than in Auth0. Open the details to see the exact values recorded. The founding grants
        are attributed to the person who received them: they predate the console, so there was no
        prior administrator to attribute them to.
      </p>
    </Card>
  );
}

/**
 * The three sections, shared by the grant form and each user's row so the two cannot
 * drift apart.
 *
 * When someone is a console admin the two population settings show Writer and are
 * locked, because that is what they actually have — iam-admin grants writer in both.
 * They unlock the moment admin is switched off, which is what makes a demotion
 * possible in a single edit: an earlier attempt at this screen disabled every lesser
 * control while a broader role was held, and since each change saved immediately,
 * nobody could be moved down at all.
 */
function AccessControls({
  access,
  onChange,
  disabled,
  adminLocked,
  adminLockedReason,
  idPrefix
}: {
  access: Access;
  onChange: (next: Access) => void;
  disabled?: boolean;
  adminLocked?: boolean;
  adminLockedReason?: string;
  idPrefix: string;
}) {
  return (
    <div className="flex flex-wrap items-start gap-6">
      <div className="flex flex-col gap-1 text-sm">
        <span className="font-medium text-slate-700">Console administration</span>
        <label className="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={access.admin}
            disabled={disabled || adminLocked}
            title={adminLocked ? adminLockedReason : undefined}
            onChange={(e) => onChange({ ...access, admin: e.target.checked })}
          />
          iam-admin
        </label>
      </div>

      {(["PARTNER", "ODC"] as Population[]).map((population) => (
        <RadioGroup
          key={population}
          // Unique per group *and* per row. The grant form and every user row render
          // these, and two groups sharing a name are one radio group to the browser —
          // so setting one person's access would clear another's.
          name={`${idPrefix}-${population}`}
          legend={populationLabel[population]}
          hint={access.admin ? "(included by iam-admin)" : undefined}
          value={access.admin ? "WRITER" : access[population]}
          disabled={disabled || access.admin}
          options={levelOptions}
          onChange={(level) => onChange({ ...access, [population]: level })}
        />
      ))}
    </div>
  );
}

type AdminUser = {
  id: string;
  email: string;
  status: string;
  roles: Role[];
};

function UserRow({
  user,
  isSelf,
  statusBusy,
  onSetStatus
}: {
  user: AdminUser;
  isSelf: boolean;
  statusBusy: boolean;
  onSetStatus: (status: "ACTIVE" | "DISABLED") => void;
}) {
  const setRoles = useSetAdminUserRoles();
  const stored = accessOf(user.roles);
  const [draft, setDraft] = useState<Access>(stored);

  const next = rolesFor(draft);
  const dirty = !sameAccess(draft, stored);
  // Every mutation replaces the whole role set, and the API refuses an empty one:
  // removing all access is what disabling the account is for.
  const wouldRemoveEverything = next.length === 0;
  // Removing your own iam-admin locks you out of the system that grants access, so
  // the backend refuses it; saying so here avoids the round trip.
  const selfDemotion = isSelf && stored.admin && !draft.admin;

  return (
    <tr className="border-b border-slate-100 align-top">
      <td className="px-2 py-2">
        {user.email}
        {isSelf ? <span className="ml-1 text-xs text-slate-500">(you)</span> : null}
      </td>
      <td className="px-2 py-2">
        <Badge tone={user.status === "ACTIVE" ? "green" : "slate"}>
          {user.status === "ACTIVE" ? "Active" : "Disabled"}
        </Badge>
      </td>
      <td className="px-2 py-2">
        <div className="flex flex-col gap-2">
          <AccessControls
            access={draft}
            onChange={setDraft}
            disabled={setRoles.isPending}
            adminLocked={isSelf && stored.admin}
            adminLockedReason="You cannot remove your own iam-admin role"
            idPrefix={`user-${user.id}`}
          />
          {dirty ? (
            <div className="flex items-center gap-2">
              <Button
                disabled={wouldRemoveEverything || selfDemotion || setRoles.isPending}
                title={
                  wouldRemoveEverything
                    ? "Disable the account instead of removing every permission"
                    : undefined
                }
                onClick={() =>
                  setRoles.mutate(
                    { id: user.id, roles: next },
                    // Re-seed the draft from what was actually stored. Promoting
                    // someone to iam-admin stores that role alone, so the draft's
                    // population levels no longer match and the row would keep
                    // offering Save after a successful save.
                    { onSuccess: () => setDraft(accessOf(next)) }
                  )
                }
              >
                {setRoles.isPending ? "Saving…" : "Save access"}
              </Button>
              <Button variant="secondary" onClick={() => setDraft(stored)}>
                Cancel
              </Button>
              {wouldRemoveEverything ? (
                <span className="text-xs text-amber-700">
                  A console user must keep some access — disable the account to revoke it.
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      </td>
      <td className="px-2 py-2">
        <Button
          variant={user.status === "ACTIVE" ? "danger" : "secondary"}
          disabled={isSelf || statusBusy}
          title={isSelf ? "You cannot disable your own account" : undefined}
          onClick={() => onSetStatus(user.status === "ACTIVE" ? "DISABLED" : "ACTIVE")}
        >
          {user.status === "ACTIVE" ? "Disable" : "Enable"}
        </Button>
      </td>
    </tr>
  );
}
